import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import type { RadioContract } from '@/mocks/superAdminContracts';
import { getPlanColor } from '@/mocks/superAdminPlans';

interface SignContractModalProps {
  contract: RadioContract;
  onClose: () => void;
  onConfirm: (contractId: number, signerName: string, signerEmail: string, signerId: string, writtenSignature: string) => void;
}

export default function SignContractModal({ contract, onClose, onConfirm }: SignContractModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerId, setSignerId] = useState('');
  const [writtenSignature, setWrittenSignature] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const canProceedStep1 = signerName.trim() && signerEmail.trim() && signerId.trim();
  const canSubmit = step === 2 && canProceedStep1 && writtenSignature.trim() && accepted && readAll;

  const handleSubmit = () => {
    if (!canSubmit) return;

    Swal.fire({
      icon: 'question',
      title: '¿Confirmar firma del contrato?',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px"><strong>${contract.contractNumber}</strong> — ${contract.radioName}</p>
          <p style="margin-bottom:4px;color:#6b7280">Plan: <strong>${contract.planName}</strong> · ${contract.clauses.length} cláusulas</p>
          <p style="margin-bottom:4px;color:#6b7280">Firmante: <strong>${signerName}</strong></p>
          <p style="margin-bottom:4px;color:#6b7280">Email: <strong>${signerEmail}</strong></p>
          <p style="margin-bottom:4px;color:#6b7280">Identificador: <strong>${signerId}</strong></p>
          <p style="color:#f97316;font-size:12px;margin-top:8px">La firma electrónica tiene validez legal. Esta acción no se puede deshacer.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Firmar Contrato',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        onConfirm(contract.id, signerName.trim(), signerEmail.trim(), signerId.trim(), writtenSignature.trim());
      }
    });
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="relative w-full max-w-[560px] bg-background-50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-background-200">
          <div>
            <h3 className="text-foreground-950 font-bold text-sm">Firma Electrónica de Contrato</h3>
            <p className="text-foreground-400 text-[10px] mt-0.5">{contract.contractNumber} · {contract.radioName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
          >
            <i className="ri-close-line text-foreground-500 text-sm"></i>
          </button>
        </div>

        {/* Steps indicator */}
        <div className="px-5 pt-4 pb-2 flex items-center gap-3">
          <div className={`flex items-center gap-1.5 ${step >= 1 ? 'text-foreground-800' : 'text-foreground-300'}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 1 ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-400'}`}>
              1
            </div>
            <span className="text-[11px] font-semibold whitespace-nowrap">Datos del Firmante</span>
          </div>
          <div className="flex-1 h-px bg-background-200"></div>
          <div className={`flex items-center gap-1.5 ${step >= 2 ? 'text-foreground-800' : 'text-foreground-300'}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${step >= 2 ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-400'}`}>
              2
            </div>
            <span className="text-[11px] font-semibold whitespace-nowrap">Firma y Aceptación</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Contract info card */}
          <div className="bg-background-100 rounded-2xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-foreground-400 text-[10px] uppercase tracking-wider">Contrato</span>
              <span className="text-foreground-400 text-[10px] font-mono">{contract.contractNumber} · v{contract.contractVersion}</span>
            </div>
            <p className="text-foreground-800 text-sm font-bold">{contract.radioName}</p>
            <div className="flex items-center gap-2 text-[11px] flex-wrap">
              <span className="text-foreground-500 font-mono">{contract.frequency}</span>
              <span className="text-foreground-300">·</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: getPlanColor(contract.planId) }}></span>
                <span className="text-foreground-600">{contract.planName}</span>
              </span>
              <span className="text-foreground-300">·</span>
              <span className="text-foreground-600">{contract.clauses.length} cláusulas</span>
            </div>
            {contract.status === 'needs_revision' && contract.revisionReason && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2 mt-2">
                <i className="ri-information-line text-orange-500 text-sm mt-0.5 shrink-0"></i>
                <p className="text-orange-700 text-[10px] leading-relaxed">{contract.revisionReason}</p>
              </div>
            )}
          </div>

          {/* Step 1: Signer data */}
          {step === 1 && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Nombre del Representante Legal</span>
                <input
                  type="text"
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Nombre completo del representante"
                  className="w-full mt-1 px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </label>
              <label className="block">
                <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Email del Firmante</span>
                <input
                  type="email"
                  value={signerEmail}
                  onChange={(e) => setSignerEmail(e.target.value)}
                  placeholder="correo@radio.com"
                  className="w-full mt-1 px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </label>
              <label className="block">
                <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Documento de Identidad</span>
                <input
                  type="text"
                  value={signerId}
                  onChange={(e) => setSignerId(e.target.value)}
                  placeholder="DNI / Pasaporte / Número de identificación"
                  className="w-full mt-1 px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                />
              </label>
              <div className="bg-background-100 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-shield-check-line text-foreground-400 text-sm mt-0.5 shrink-0"></i>
                <div>
                  <p className="text-foreground-500 text-[10px] font-semibold">IP del Firmante (Futura)</p>
                  <p className="text-foreground-300 text-[10px] font-mono">Se registrará automáticamente al firmar · 000.000.000.000</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Signature + acceptance */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Signer summary */}
              <div className="bg-background-100 rounded-xl p-3 space-y-1">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Firmante:</span>
                  <span className="text-foreground-700 font-semibold">{signerName}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Email:</span>
                  <span className="text-foreground-600">{signerEmail}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Documento:</span>
                  <span className="text-foreground-600">{signerId}</span>
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-foreground-400">Fecha:</span>
                  <span className="text-foreground-600">{new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>

              {/* Written signature */}
              <label className="block">
                <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Firma Escrita (Escribí tu nombre completo)</span>
                <input
                  type="text"
                  value={writtenSignature}
                  onChange={(e) => setWrittenSignature(e.target.value)}
                  placeholder="Escribí tu nombre y apellido como firma"
                  className="w-full mt-1 px-3 py-3 bg-background-50 border-2 border-dashed border-background-300 rounded-xl text-lg text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-400 transition-colors font-signature"
                  style={{ fontFamily: "'Caveat', cursive", fontSize: '22px' }}
                />
                <p className="text-foreground-300 text-[9px] mt-1">Esta firma manuscrita digital tiene validez legal como manifestación de consentimiento.</p>
              </label>

              {/* Checkboxes */}
              <div className="space-y-2.5">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-background-100 transition-colors border border-background-200">
                  <input
                    type="checkbox"
                    checked={readAll}
                    onChange={(e) => setReadAll(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-background-300 accent-foreground-950 cursor-pointer"
                  />
                  <div>
                    <p className="text-foreground-800 text-[11px] font-semibold">He leído todas las cláusulas del contrato</p>
                    <p className="text-foreground-400 text-[10px] mt-0.5">
                      Declaro haber leído y comprendido las {contract.clauses.length} cláusulas del presente Contrato.
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-background-100 transition-colors border border-background-200">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-background-300 accent-foreground-950 cursor-pointer"
                  />
                  <div>
                    <p className="text-foreground-800 text-[11px] font-semibold">Acepto todos los términos y condiciones</p>
                    <p className="text-foreground-400 text-[10px] mt-0.5">
                      La firma electrónica del presente Contrato tiene plena validez legal y fuerza ejecutiva según la legislación aplicable.
                    </p>
                  </div>
                </label>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                <i className="ri-scales-3-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
                <p className="text-amber-700 text-[10px] leading-relaxed">
                  Al firmar este contrato, La Radio acepta todas las cláusulas aquí contenidas, incluyendo limitaciones de responsabilidad, dependencia de terceros, condición "AS IS" del servicio, y todas las obligaciones legales descritas en el documento. La firma electrónica es vinculante y ejecutable.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-background-200">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continuar a Firma
                <i className="ri-arrow-right-line ml-1.5 text-sm"></i>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-1 text-sm"></i>
                Volver
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="px-5 py-2 rounded-xl bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center gap-1.5">
                    <i className="ri-quill-pen-line text-sm"></i>
                    Firmar Contrato
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}